import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { SypacInput, SypacText } from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import {
	ProductService,
	ProductStoreInterface,
	RequestProductsAdminParams,
} from '../../../../services/product.services';
import StoreIcon from '../../../../assets/StoreIcon';
import { ChevronRightIcon } from '../../../../assets/ChevronRightIcon';
import { BlueBox } from '../../../../assets/BlueBox';
import useDebounce from '../../../../hooks/useDebounce';
import { ProductInterface } from '../../../../interfaces/product.interface';
import Flag from 'react-world-flags';
import { SectionProps } from '../addNewContract.interface';
import { cn } from '../../../../lib/utils';

function useOutsideClickCloser(
	pairs: Array<{
		inputRef: React.RefObject<HTMLElement>;
		menuRef?: React.RefObject<HTMLElement>;
		close: () => void;
	}>,
) {
	useEffect(() => {
		const onDocMouseDown = (e: MouseEvent) => {
			const target = e.target as Node;
			pairs.forEach(({ inputRef, menuRef, close }) => {
				const onInput = !!inputRef.current?.contains(target);
				const inMenu = !!menuRef?.current?.contains(target);
				if (!onInput && !inMenu) close();
			});
		};
		document.addEventListener('mousedown', onDocMouseDown);
		return () => document.removeEventListener('mousedown', onDocMouseDown);
	}, [pairs]);
}

const ProductSection: React.FC<SectionProps> = ({ formik }) => {
	const { t } = useTranslate();

	const [productDisplay, setProductDisplay] = useState<string>('');
	const [productOptions, setProductOptions] = useState<ProductInterface[]>([]);

	const productsByStoreId = useMemo(() => {
		const map: Record<string, number> = {};

		productOptions.forEach((product) => {
			if (product.productBaseId == null) return;

			const key = String(product.productBaseId);
			map[key] = (map[key] || 0) + 1;
		});

		return map;
	}, [productOptions]);

	const filteredProductOptions = useMemo(() => {
		const selectedStoreId = formik.values.store;
		if (!selectedStoreId) {
			return productOptions;
		}

		return productOptions.filter(
			(p) => String(p.productBaseId) === String(selectedStoreId),
		);
	}, [productOptions, formik.values.store]);

	const [showProductOptions, setShowProductOptions] = useState<boolean>(false);
	const productWrapperRef = useRef<HTMLDivElement | null>(null);
	const debouncedProduct = useDebounce(productDisplay, 500);

	const [sellerStores, setSellerStores] = useState<ProductStoreInterface[]>([]);
	const [storeDisplay, setStoreDisplay] = useState<string>('');
	const [showStores, setShowStores] = useState(false);
	const storeInputRef = useRef<HTMLInputElement | null>(null);
	const storeMenuRef = useRef<HTMLDivElement | null>(null);
	const productInputRef = useRef<HTMLInputElement | null>(null);
	const productMenuRef = useRef<HTMLDivElement | null>(null);

	const resetProductSelection = () => {
		setProductDisplay('');
		formik.setFieldValue('productName', '');
		formik.setFieldValue('productLabel', '');
		formik.setFieldValue('productType', '');
		formik.setFieldValue('productSize', '');
	};

	useOutsideClickCloser([
		{
			inputRef: productInputRef,
			menuRef: productMenuRef,
			close: () => setShowProductOptions(false),
		},
		{
			inputRef: storeInputRef,
			menuRef: storeMenuRef,
			close: () => setShowStores(false),
		},
	]);

	useEffect(() => {
		let live = true;
		(async () => {
			if (formik.values.productName) {
				setShowProductOptions(false);
				return;
			}

			const sellerId = Number(formik.values.sellerCompany || 0);
			if (!sellerId) {
				setProductOptions([]);
				return;
			}

			try {
				const params: RequestProductsAdminParams = {
					status: 'approved',
					limit: 200,
					companyId: sellerId,
				};

				if (debouncedProduct.trim()) {
					params.search = debouncedProduct;
				}

				const { data } = await ProductService.getListAdmin(params);
				if (!live) return;

				setProductOptions(data?.items ?? []);
			} catch (err) {
				console.error('Failed to fetch products:', err);
			}
		})();

		return () => {
			live = false;
		};
	}, [
		debouncedProduct,
		formik.values.sellerCompany,
		formik.values.productName,
	]);

	const fetchSellerStores = useCallback(async (): Promise<
		ProductStoreInterface[]
	> => {
		const sellerId = Number(formik.values.sellerCompany);
		if (!sellerId) return [];
		try {
			const { data } = await ProductService.getProductStores(
				{ limit: 100, offset: 0, companyIds: [sellerId] },
				'admin',
			);
			const items = data?.items ?? [];
			setSellerStores(items);
			return items;
		} catch (err) {
			console.error('Failed to fetch stores:', err);
			return [];
		}
	}, [formik.values.sellerCompany]);

	useEffect(() => {
		setSellerStores([]);
		setStoreDisplay('');
		formik.setFieldValue('store', '');
		formik.setFieldValue('storeName', '');
		formik.setFieldValue('storeAddress', '');
		formik.setFieldValue('storeCountryCode', '');
		setShowStores(false);
		fetchSellerStores().then(() => {});
	}, [fetchSellerStores]); // eslint-disable-line

	useEffect(() => {
		setProductDisplay('');
		formik.setFieldValue('productName', '');
		formik.setFieldValue('productLabel', '');
		formik.setFieldValue('productType', '');
		formik.setFieldValue('productSize', '');
	}, [formik.values.sellerCompany]); // eslint-disable-line

	const handleProductInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		setProductDisplay(val);
		formik.setFieldValue('productName', '');
		formik.setFieldValue('productLabel', '');
		formik.setFieldValue('productType', '');
		formik.setFieldValue('productSize', '');
		formik.setFieldValue('store', '');
		formik.setFieldValue('storeName', '');
		formik.setFieldValue('storeAddress', '');
		formik.setFieldValue('storeCountryCode', '');
		setStoreDisplay('');
	};

	const handleStoreInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		setStoreDisplay(val);

		if (val === '') {
			resetProductSelection();
			formik.setFieldValue('store', '');
			formik.setFieldValue('storeName', '');
			formik.setFieldValue('storeAddress', '');
			formik.setFieldValue('storeCountryCode', '');
		}
	};

	const handleProductFocusOrClick = () => {
		setShowProductOptions(true);
	};

	const pickProduct = async (p: ProductInterface) => {
		const translatedName =
			p.translations?.find((t) => t.language === 'en')?.name || p.name;

		setProductDisplay(translatedName);
		formik.setFieldValue('productName', p.id);
		formik.setFieldValue('productLabel', translatedName);
		formik.setFieldValue('productType', p.type ?? '');
		formik.setFieldValue('productSize', p.size ?? '');
		setShowProductOptions(false);

		const stores =
			sellerStores.length > 0 ? sellerStores : await fetchSellerStores();

		const matchedStore = stores.find(
			(s) => String(s.id) === String(p.productBaseId),
		);

		if (matchedStore) {
			formik.setFieldValue('store', matchedStore.id);
			formik.setFieldValue('storeName', matchedStore.name || '');
			formik.setFieldValue('storeAddress', matchedStore.address || '');
			formik.setFieldValue('storeCountryCode', matchedStore.countryCode || '');
			setStoreDisplay(matchedStore.name || '');
			setShowStores(false);
		} else {
			formik.setFieldValue('store', '');
			formik.setFieldValue('storeName', '');
			formik.setFieldValue('storeAddress', '');
			formik.setFieldValue('storeCountryCode', '');
			setStoreDisplay('');
		}
	};

	const pickStore = (s: ProductStoreInterface) => {
		resetProductSelection();

		formik.setFieldValue('store', s.id);
		formik.setFieldValue('storeName', s.name || '');
		formik.setFieldValue('storeAddress', s.address || '');
		formik.setFieldValue('storeCountryCode', s.countryCode || '');
		setStoreDisplay(s.name || '');
		setShowStores(false);
	};

	useEffect(() => {
		if (!formik.values.store) {
			return;
		}

		const matchedStore = sellerStores.find(
			(s) => String(s.id) === String(formik.values.store),
		);

		if (matchedStore) {
			setStoreDisplay(matchedStore.name || '');
		}
	}, [sellerStores, formik.values.store]);

	return (
		<>
			<SypacText variant="body-regular-medium">
				<p className="text-gray-40 text-[14px] leading-[16px] mx-3">
					<T keyName="adminContracts.selectProduct">Select product</T>
				</p>
			</SypacText>

			<div
				ref={productWrapperRef}
				className="flex flex-col gap-3 items-center justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 mx-3 p-[10px]"
			>
				<SypacInput
					error={!!(formik.touched.productName && formik.errors.productName)}
					className="order-2 relative w-full"
				>
					<SypacText className="mb-2" variant="overline-normal-large">
						<p>
							<T keyName="adminContracts.productName">Product name</T>{' '}
							<span className="text-red">*</span>
						</p>
					</SypacText>
					<input
						ref={productInputRef}
						className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
							formik.touched.productName && formik.errors.productName
								? 'border-red-orange'
								: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
						}`}
						name="productNameInput"
						type="text"
						placeholder={t('adminContracts.selectProduct', 'Select product')}
						value={`${productDisplay}${
							formik.values.productType || formik.values.productSize
								? '    •    '
								: ''
						}${formik.values.productType ?? ''}${
							formik.values.productType && formik.values.productSize ? ' ' : ''
						}${
							formik.values.productSize ? `(${formik.values.productSize})` : ''
						}`}
						onChange={handleProductInputChange}
						onFocus={handleProductFocusOrClick}
						onClick={handleProductFocusOrClick}
					/>

					<span
						className={`absolute top-[42px] right-3 flex transform transition-all duration-150 ease-in-out ${
							showProductOptions ? 'rotate-90' : ''
						}`}
						onClick={() => setShowProductOptions((s) => !s)}
						role="button"
						aria-label="Toggle product options"
					>
						<ChevronRightIcon />
					</span>

					{showProductOptions && (
						<div
							ref={productMenuRef}
							className="absolute left-0 right-0 top-[70px] flex flex-col w-full max-h-72 bg-white border border-solid border-gray-10 rounded-[10px] overflow-hidden z-50 box-border shadow-2xl"
						>
							{!formik.values.sellerCompany ? (
								<div className="flex items-center justify-center h-[48px]">
									<SypacText variant="body-normal-medium">
										<p className="text-gray-80 text-[14px] leading-[16px]">
											<T keyName="adminContracts.searchForSellerCompany">
												Search for Seller company
											</T>
										</p>
									</SypacText>
								</div>
							) : filteredProductOptions.length === 0 ? (
								<div className="flex items-center justify-center h-[48px]">
									<SypacText variant="body-normal-medium">
										<p className="text-gray-80 text-[14px] leading-[16px]">
											<T keyName="common.noResults">No results</T>
										</p>
									</SypacText>
								</div>
							) : (
								<>
									<SypacText variant="body-regular-medium">
										<p className="text-gray-40 text-[12px] leading-[14px] px-3 pt-2 pb-1">
											<T keyName="adminContracts.allProducts">All products</T>
										</p>
									</SypacText>
									<nav className="flex flex-col w-full p-1 pr-2 overflow-y-scroll overflow-x-hidden">
										{filteredProductOptions.map((p) => (
											<div
												key={p.id}
												className="flex gap-3 items-center px-1 py-0 mb-[3px] mr-2 bg-white border border-solid border-white rounded-[8px] cursor-pointer hover:bg-[#FAFAFA] hover:border-[#E8E8E8]"
												onClick={() => pickProduct(p)}
											>
												<div
													style={{ backgroundImage: `url(${p.photoUrl})` }}
													className="flex flex-none w-[44px] h-[44px] bg-no-repeat bg-cover rounded-[5px] border border-solid border-gray-10"
													role="img"
													aria-label={
														p.translations?.find((t) => t.language === 'en')
															?.name
													}
												/>

												<div className="flex flex-col gap-2">
													<div className="flex items-center gap-3">
														<SypacText variant="body-regular-medium">
															<p className="text-gray-80 text-[16px] leading-[18px]">
																{
																	p.translations?.find(
																		(t) => t.language === 'en',
																	)?.name
																}
															</p>
														</SypacText>
														{`${p?.type ? `${p?.type}` : ''} ${
															p?.size ? `(${p?.size})` : ''
														}`.trim() ? (
															<div className="w-[2px] h-[2px] bg-gray-60 rounded-full" />
														) : null}
														<SypacText variant="body-regular-medium">
															<p className="text-gray-80">{`${p?.type ?? ''} ${
																p?.size ? `(${p?.size})` : ''
															}`}</p>
														</SypacText>
													</div>
													<div className="flex items-center gap-3">
														<SypacText variant="body-regular-medium">
															<p className="text-gray-40 text-[12px] leading-[14px]">
																{p.company?.name}
															</p>
														</SypacText>
														<div className="w-[2px] h-[2px] bg-gray-60 rounded-full" />
														<div className="flex items-center gap-1">
															<span className="mt-[2px]">
																<Flag
																	className="object-cover border border-solid border-gray-10 rounded"
																	code={p.countryCode}
																	width={20}
																	height={14}
																/>
															</span>
															<SypacText variant="body-regular-medium">
																<p className="text-gray-40 text-[12px] leading-[14px]">
																	{p.address}
																</p>
															</SypacText>
														</div>
													</div>
												</div>
											</div>
										))}
									</nav>
								</>
							)}
						</div>
					)}

					<div className="absolute top-[36px] left-[10px]">
						<BlueBox color="#A2A2A2" />
					</div>
					{formik?.touched.productName && formik?.errors.productName ? (
						<span className="input-error">{formik.errors.productName}</span>
					) : null}
				</SypacInput>

				<SypacInput
					error={!!(formik.touched.store && formik.errors.store)}
					className="order-1 relative w-full"
				>
					<SypacText className="mb-2" variant="overline-normal-large">
						<p>
							<T keyName="adminContracts.store">Store</T>
						</p>
					</SypacText>

					<input
						ref={storeInputRef}
						className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
							formik.touched.store && formik.errors.store
								? 'border-red-orange'
								: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
						}`}
						name="storeInput"
						type="text"
						placeholder={t('adminContracts.selectStore', 'Select store')}
						value={storeDisplay}
						onChange={handleStoreInputChange}
						onClick={() => setShowStores((s) => !s)}
					/>

					<span
						className={`absolute top-[42px] right-3 flex transform transition-all duration-150 ease-in-out ${
							showStores ? 'rotate-90' : ''
						}`}
					>
						<ChevronRightIcon />
					</span>

					{showStores && (
						<div
							ref={storeMenuRef}
							className="absolute left-0 right-0 top-[70px] flex flex-col w-full max-h-72 bg-white border border-solid border-gray-10 rounded-[10px] overflow-hidden z-50 box-border shadow-2xl"
						>
							{!formik.values.sellerCompany ? (
								<div className="flex items-center justify-center h-[48px]">
									<SypacText variant="body-normal-medium">
										<p className="text-gray-80 text-[14px] leading-[16px]">
											<T keyName="adminContracts.searchForSellerCompany">
												Search for Seller company
											</T>
										</p>
									</SypacText>
								</div>
							) : sellerStores.length === 0 ? (
								<div className="flex items-center justify-center h-[48px]">
									<SypacText variant="body-normal-medium">
										<p className="text-gray-80 text-[14px] leading-[16px]">
											<T keyName="common.noResults">No results</T>
										</p>
									</SypacText>
								</div>
							) : (
								<>
									<SypacText variant="body-regular-medium">
										<p className="text-gray-40 text-[12px] leading-[14px] px-[10px] pt-2 pb-2">
											<T keyName="adminContracts.recentStores">Recent stores</T>
										</p>
									</SypacText>
									<nav className="flex flex-col w-full p-1 pr-2 overflow-y-scroll overflow-x-hidden">
										{sellerStores.map((s) => {
											const productCountForStore =
												productsByStoreId[String(s.id)] ?? 0;
											const isDisabled = productCountForStore === 0;

											return (
												<div
													key={s.id}
													aria-disabled={isDisabled}
													className={cn(
														'flex gap-3 items-start justify-between p-1 mb-[3px] mr-2 bg-white border border-solid border-white rounded-[8px]',
														isDisabled
															? 'opacity-40 cursor-not-allowed'
															: 'cursor-pointer hover:bg-[#FAFAFA] hover:border-[#E8E8E8]',
													)}
													onClick={() => {
														if (isDisabled) return;
														pickStore(s);
													}}
												>
													<div className="flex flex-col gap-2">
														<SypacText variant="body-regular-medium">
															<p className="text-gray-80 text-[16px] leading-[18px]">
																{s.name}
															</p>
														</SypacText>

														<div className="flex items-center gap-2">
															<Flag
																className="mb-[2px] object-cover border border-solid border-gray-10 rounded"
																code={s.countryCode}
																width={20}
																height={14}
															/>
															<SypacText variant="body-regular-medium">
																<p className="text-gray-40 text-[12px] leading-[14px]">
																	{s.address}
																</p>
															</SypacText>
														</div>
													</div>

													<div className="flex items-center justify-center bg-gray-10 rounded-[6px] px-[6px] py-[2px]">
														<SypacText variant="body-regular-medium">
															<p
																className={`text-[12px] leading-[16px] ${
																	productCountForStore === 0
																		? 'text-gray-40'
																		: 'text-gray-80'
																}`}
															>
																{productCountForStore}{' '}
																<T keyName="productStores.products">products</T>
															</p>
														</SypacText>
													</div>
												</div>
											);
										})}
									</nav>
								</>
							)}
						</div>
					)}

					<div className="absolute top-[36px] left-[10px]">
						<StoreIcon />
					</div>

					{formik?.touched.store && formik?.errors.store ? (
						<span className="input-error">{formik.errors.store}</span>
					) : null}
				</SypacInput>
			</div>
		</>
	);
};

export default ProductSection;
